import React from 'react';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
} from '@mui/material';

export interface UtilityMetricSectionProps {
  title: string;
  description?: string;
  /** Optional section icon (e.g. utility symbol). */
  Icon?: React.ComponentType<SvgIconProps>;
  consumptionLabel: string;
  amountLabel: string;
  consumptionValue: string;
  amountValue: string;
  onConsumptionChange: (value: string) => void;
  onAmountChange: (value: string) => void;
  disabled?: boolean;
}

const UtilityMetricSection: React.FC<UtilityMetricSectionProps> = ({
  title,
  description,
  Icon,
  consumptionLabel,
  amountLabel,
  consumptionValue,
  amountValue,
  onConsumptionChange,
  onAmountChange,
  disabled,
}) => {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        avatar={
          Icon ? (
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0, 0, 254, 0.08)',
              }}
            >
              <Icon sx={{ color: 'primary.main', fontSize: 26 }} />
            </Box>
          ) : undefined
        }
        title={
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
            {title}
          </Typography>
        }
        subheader={
          description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.45 }}>
              {description}
            </Typography>
          ) : undefined
        }
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          alignItems: 'flex-start',
          '& .MuiCardHeader-avatar': { mr: 2, mt: 0.25 },
          '& .MuiCardHeader-content': { overflow: 'hidden' },
        }}
      />
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: 2.5,
          pt: 0,
          pb: 2.5,
          '&:last-child': { pb: 2.5 },
        }}
      >
        <StackFields
          consumptionLabel={consumptionLabel}
          amountLabel={amountLabel}
          consumptionValue={consumptionValue}
          amountValue={amountValue}
          onConsumptionChange={onConsumptionChange}
          onAmountChange={onAmountChange}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
};

const StackFields: React.FC<{
  consumptionLabel: string;
  amountLabel: string;
  consumptionValue: string;
  amountValue: string;
  onConsumptionChange: (value: string) => void;
  onAmountChange: (value: string) => void;
  disabled?: boolean;
}> = ({
  consumptionLabel,
  amountLabel,
  consumptionValue,
  amountValue,
  onConsumptionChange,
  onAmountChange,
  disabled,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1.75,
      mt: 0.25,
    }}
  >
    <TextField
      label={consumptionLabel}
      type="number"
      size="small"
      fullWidth
      value={consumptionValue}
      onChange={(e) => onConsumptionChange(e.target.value)}
      disabled={disabled}
      inputProps={{ min: 0, step: 'any' }}
    />
    <TextField
      label={amountLabel}
      type="number"
      size="small"
      fullWidth
      value={amountValue}
      onChange={(e) => onAmountChange(e.target.value)}
      disabled={disabled}
      inputProps={{ min: 0, step: 'any' }}
    />
  </Box>
);

export default UtilityMetricSection;
